import { defineStore } from "pinia";
import { ref } from "vue";
import { api } from "@/services/api.config";

export type SeoProjectReleaseStatus = "prod" | "beta";

export interface SeoProjectAccess {
  slug: string;
  name_key: string;
  name: string;
  status: SeoProjectReleaseStatus;
  accessible: boolean;
}

const BETA_UNTIL_LOADED = new Set(["position-checker", "cwv"]);

export const useSeoProjectsStore = defineStore("seoProjects", () => {
  const projects = ref<SeoProjectAccess[]>([]);
  const isBetaTester = ref(false);
  const loadedMode = ref<"public" | "auth" | null>(null);

  let inflight: Promise<void> | null = null;
  let inflightMode: "public" | "auth" | null = null;

  function noteRole(role: string | null | undefined) {
    if (role && role !== "user") {
      isBetaTester.value = true;
    }
  }

  function projectBySlug(slug: string): SeoProjectAccess | undefined {
    return projects.value.find((project) => project.slug === slug);
  }

  function isBeta(slug: string): boolean {
    const project = projectBySlug(slug);
    if (project) {
      return project.status === "beta";
    }

    return loadedMode.value === null && BETA_UNTIL_LOADED.has(slug);
  }

  function canAccess(slug: string): boolean {
    const project = projectBySlug(slug);
    if (project) {
      return project.accessible;
    }

    if (isBeta(slug)) {
      return isBetaTester.value;
    }

    return true;
  }

  function applyAuthPayload(data: {
    is_beta_tester?: boolean;
    projects?: SeoProjectAccess[];
  }) {
    projects.value = data.projects ?? [];
    isBetaTester.value = !!data.is_beta_tester;
    loadedMode.value = "auth";
  }

  async function ensureLoaded(authenticated: boolean): Promise<void> {
    const mode = authenticated ? "auth" : "public";
    if (loadedMode.value === "auth" || loadedMode.value === mode) {
      return;
    }
    if (
      inflight &&
      (inflightMode === mode || inflightMode === "auth")
    ) {
      return inflight;
    }

    inflightMode = mode;
    inflight = (async () => {
      try {
        if (mode === "auth") {
          const { data } = await api.get("/api/seo-projects");
          applyAuthPayload(data);
          return;
        }

        const { data } = await api.get("/api/seo-projects/public");
        const tester = isBetaTester.value;
        projects.value = (data.projects ?? []).map(
          (project: { slug: string; status: SeoProjectReleaseStatus }) => ({
            slug: project.slug,
            name_key: "",
            name: project.slug,
            status: project.status,
            accessible: project.status !== "beta" || tester,
          }),
        );
        loadedMode.value = "public";
      } catch {
        // Меню лишається на консервативному дефолті: Position Checker прихований,
        // доки каталог не завантажиться. Роль уже могла позначити бета-тестера.
      } finally {
        inflight = null;
        inflightMode = null;
      }
    })();

    return inflight;
  }

  function setProjectStatus(slug: string, status: SeoProjectReleaseStatus) {
    const project = projectBySlug(slug);
    if (!project) {
      return;
    }
    project.status = status;
    project.accessible = status !== "beta" || isBetaTester.value;
  }

  return {
    projects,
    isBetaTester,
    loadedMode,
    noteRole,
    isBeta,
    canAccess,
    ensureLoaded,
    applyAuthPayload,
    setProjectStatus,
  };
});
